import { useState, useEffect, useMemo } from "react";
import { Modal, Form, Input, Switch, Upload, Button, Progress, notification, AutoComplete, Alert, Tooltip, Typography, Radio } from "antd";
import { InboxOutlined, FileZipOutlined, DeleteOutlined, CheckCircleFilled, HddOutlined, DesktopOutlined } from "@ant-design/icons";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import "./OtaUploadModal.scss";

const { Text } = Typography;

const ACCEPTED_EXTENSIONS = ['.zip', '.tar.gz', '.tgz', '.tar.bz2', '.tar.xz', '.tar', '.gz'];

// Mismo convenio que el almacén: alfanuméricos, punto, guion y guion bajo
const SEGMENT = /^[a-zA-Z0-9._-]+$/;
const RESERVED = ['latest', 'latest.json', 'manifest.json'];

/**
 * Publicación de una versión de firmware.
 *
 * El destino llega relleno con la carpeta abierta y se puede cambiar: los nombres
 * existentes se ofrecen como sugerencia para no crear carpetas por una errata, ya
 * que en el almacén una carpeta nueva nace con su primera versión.
 */
function OtaUploadModal({ open, product, channel, target, tree = [], existingVersions = [], intent = 'version', onCancel, onUploaded, lang }) {

  // ===== 1. ESTADOS =====
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const [ui, setUI] = useState({ uploading: false, progress: 0 });

  const values = Form.useWatch([], form) ?? {};

  // ===== 2. DERIVADOS =====
  // Sugerencias sacadas del árbol: lo que ya existe se escribe solo
  const firmwareOptions = useMemo(
    () => tree.map(node => ({ value: node.name })),
    [tree]
  );

  const channelOptions = useMemo(() => {
    const node = tree.find(item => item.name === values.product);
    return (node?.children ?? []).map(child => ({ value: child.name }));
  }, [tree, values.product]);

  /**
   *  La variante es un único nivel: se ofrecen las que ya tiene el canal
   */ 
  const variantOptions = useMemo(() => {
    const firmwareNode = tree.find(item => item.name === values.product);
    const channelNode = (firmwareNode?.children ?? []).find(child => child.name === values.channel);

    return (channelNode?.children ?? []).map(child => ({ value: child.name }));
  }, [tree, values.product, values.channel]);

  /**
   *  Destino completo de la versión, incluyendo firmware, canal, target y versión
   */
  const destination = [values.product, values.channel, values.target, values.version]
    .filter(Boolean)
    .join('/');

  // El aviso de duplicado solo tiene sentido si no se ha cambiado de destino
  const sameContext = values.product === product && values.channel === channel && (values.target || null) === (target || null);
  const versionExists = sameContext && values.version && existingVersions.includes(values.version.trim());

  /**
   *  Qué parte del destino no existe todavía: se creará al publicar
   */
  const creating = useMemo(() => {
    const firmwareNode = tree.find(item => item.name === values.product);
    if (values.product && !firmwareNode){
      return 'firmware';
    }

    const channelNode = (firmwareNode?.children ?? []).find(item => item.name === values.channel);

    if (values.channel && !channelNode){
      return 'channel';
    }
    if (values.target && !(channelNode?.children ?? []).some(item => item.name === values.target)) {
      return 'variant';
    }

    return null;
  }, [tree, values.product, values.channel, values.target]);

  const creatingLabel = {
    firmware: lang.handle('Se creará el firmware'),
    channel: lang.handle('Se creará el canal'),
    variant: lang.handle('Se creará la variante'),
  }[creating];

  const title = {
    firmware: lang.handle('Nuevo firmware'),
    channel: lang.handle('Nuevo canal'),
    version: lang.handle('Nueva versión'),
  }[intent];

  // ===== 3. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));

  const isAcceptedFile = (name) => ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext));

  const formatSize = (bytes) => bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

  /** Deduce la versión del nombre del paquete: kid-admin-2026.7.1.tar.gz -> 2026.7.1 */
  const guessVersion = (filename) => {
    const extension = ACCEPTED_EXTENSIONS.find(ext => filename.endsWith(ext)) ?? '';
    const base = filename.slice(0, filename.length - extension.length);
    const match = base.match(/(\d[\d._-]*\d|\d)$/);

    return match ? match[1] : null;
  };

  /** Valida un segmento con el mismo criterio que aplica el backend. */
  const segmentRule = (message) => ({
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      if (!SEGMENT.test(value) || RESERVED.includes(value)) return Promise.reject(new Error(message));
      return Promise.resolve();
    }
  });

  const takeFile = (selected) => {
    if (!isAcceptedFile(selected.name)) {
      notification["error"]({
        message: lang.handle('Formato no permitido') + ': ' + ACCEPTED_EXTENSIONS.join(', ')
      });
      return false;
    }

    setFile(selected);

    // Si el nombre trae la versión y el campo está vacío, se rellena solo
    if (!form.getFieldValue('version')) {
      const guessed = guessVersion(selected.name);
      if (guessed) form.setFieldValue('version', guessed);
    }

    return false;
  };

  // ===== 4. HANDLERS =====
  const closeModal = () => {
    if (ui.uploading) return;

    form.resetFields();
    setFile(null);
    setUI({ uploading: false, progress: 0 });
    onCancel();
  };

  const submit = async () => {
    let data;
    try {
      data = await form.validateFields();
    } catch {
      return;
    }

    if (!file) {
      notification["error"]({ message: lang.handle('Selecciona el archivo de firmware') });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('setLatest', String(data.setLatest ?? false));
    if (data.target) formData.append('target', data.target);

    // El destino solo se declara al crear el firmware: las versiones siguientes
    // heredan el del software, que se cambia desde el explorador.
    if (creating === 'firmware') formData.append('audience', data.audience ?? 'device');

    updateUI('uploading', true);
    try {
      const url = `ota/versions/${data.product}/${data.channel}/${data.version.trim()}`;
      const res = await service.upload(url, formData, (percent) => updateUI('progress', percent));

      if (!res) return;

      if (res.data.ok === false) {
        notification["error"]({ message: lang.handle(res.data.message) });
        return;
      }

      notification["success"]({ message: lang.handle('Versión subida correctamente') });
      form.resetFields();
      setFile(null);
      onUploaded();

    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      setUI({ uploading: false, progress: 0 });
    }
  };

  // ===== 5. EFFECTS =====
  useEffect(() => {
    if (open) {
      // Se hereda el contexto salvo lo que se viene a crear, que se deja en blanco.
      // Una versión nueva arranca en la variante abierta, que se puede cambiar o crear aquí.
      form.setFieldsValue({
        product: intent === 'firmware' ? '' : product ?? '',
        channel: intent === 'firmware' || intent === 'channel' ? '' : channel ?? '',
        target: intent === 'version' ? target ?? '' : '',
        version: '',
        setLatest: false,
        audience: 'device'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product, channel, target, intent]);

  // ===== 6. RENDER =====
  return (
    <Modal
      title={title}
      open={open}
      onCancel={closeModal}
      onOk={submit}
      okText={lang.handle('Publicar')}
      cancelText={lang.handle('Cancelar')}
      confirmLoading={ui.uploading}
      cancelButtonProps={{ disabled: ui.uploading }}
      maskClosable={!ui.uploading}
      keyboard={!ui.uploading}
      width={620}
      destroyOnClose
      className="ota-upload"
    >
      <Form
        form={form}
        layout="horizontal"
        labelAlign="left"
        labelCol={{ flex: '130px' }}
        wrapperCol={{ flex: 'auto' }}
        colon={false}
        disabled={ui.uploading}
        requiredMark={false}
      >

        {/* El paquete primero: de él se deduce la versión */}
        {!file
          ? <Upload.Dragger
            className="ota-upload__dropzone"
            multiple={false}
            showUploadList={false}
            beforeUpload={takeFile}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{lang.handle('Arrastra aquí el paquete de firmware')}</p>
            <p className="ant-upload-hint">{ACCEPTED_EXTENSIONS.join('  ·  ')}</p>
          </Upload.Dragger>
          : <div className="ota-upload__file">
            <FileZipOutlined className="ota-upload__file-icon" />
            <span className="ota-upload__file-text">
              <span className="ota-upload__file-name">{file.name}</span>
              <span className="ota-upload__file-size">{formatSize(file.size)}</span>
            </span>
            <Tooltip title={lang.handle('Quitar')}>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                disabled={ui.uploading}
                onClick={() => setFile(null)}
              />
            </Tooltip>
          </div>
        }

        <div className="ota-upload__row">
          <Form.Item
            name="product"
            label={lang.handle('Firmware')}
            rules={[
              { required: true, message: lang.handle('Campo obligatorio') },
              segmentRule(lang.handle('Solo se admiten letras, números, punto, guion y guion bajo'))
            ]}
          >
            <AutoComplete
              options={firmwareOptions}
              filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
              placeholder={lang.handle('Nuevo o existente')}
              autoFocus={intent === 'firmware'}
            />
          </Form.Item>

          <Form.Item
            name="channel"
            label={lang.handle('Canal')}
            rules={[
              { required: true, message: lang.handle('Campo obligatorio') },
              segmentRule(lang.handle('Solo se admiten letras, números, punto, guion y guion bajo'))
            ]}
          >
            <AutoComplete
              options={channelOptions}
              filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
              placeholder={lang.handle('Nuevo o existente')}
              autoFocus={intent === 'channel'}
            />
          </Form.Item>
        </div>

        <div className="ota-upload__row">
          <Form.Item
            name="target"
            label={lang.handle('Variante (opcional)')}
            tooltip={lang.handle('Distingue compilaciones del mismo canal, por ejemplo rpi4')}
            rules={[
              segmentRule(lang.handle('Solo se admiten letras, números, punto, guion y guion bajo'))
            ]}
          >
            <AutoComplete
              options={variantOptions}
              filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
              placeholder={lang.handle('Sin variante')}
            />
          </Form.Item>

          <Form.Item
            name="version"
            label={lang.handle('Versión')}
            validateStatus={versionExists ? 'error' : undefined}
            help={versionExists ? lang.handle('Esa versión ya existe en este destino') : undefined}
            rules={[
              { required: true, message: lang.handle('Campo obligatorio') },
              segmentRule(lang.handle('Solo se admiten letras, números, punto, guion y guion bajo'))
            ]}
          >
            <Input placeholder="2026.7.1" />
          </Form.Item>
        </div>

        {/* Dónde va a quedar exactamente lo que se publica */}
        <div className={`ota-upload__destination ${creating ? 'ota-upload__destination--new' : ''}`}>
          <span className="ota-upload__destination-label">{lang.handle('Se publicará en')}</span>
          <Text code className="ota-upload__destination-path">{destination || '—'}</Text>
          {creating &&
            <Tooltip title={lang.handle('Las carpetas del almacén nacen con su primera versión')}>
              <span className="ota-upload__destination-new">{creatingLabel}</span>
            </Tooltip>
          }
        </div>

        {/* Solo al crear el firmware: el destino es del software, no de la versión */}
        {creating === 'firmware' &&
          <div className="ota-upload__audience">
            <span className="ota-upload__audience-label">{lang.handle('¿A qué va dirigido este software?')}</span>

            <Form.Item name="audience" noStyle>
              <Radio.Group className="ota-upload__audience-options">
                <Radio value="device">
                  <span className="ota-upload__audience-option">
                    <HddOutlined />
                    <span>
                      <strong>{lang.handle('Equipos')}</strong>
                      <em>{lang.handle('Core comprueba que el equipo esté registrado y con la suscripción en regla')}</em>
                    </span>
                  </span>
                </Radio>

                <Radio value="user">
                  <span className="ota-upload__audience-option">
                    <DesktopOutlined />
                    <span>
                      <strong>{lang.handle('Aplicaciones de usuario')}</strong>
                      <em>{lang.handle('Se descarga sin comprobar nada en Core, porque no hay equipo que comprobar')}</em>
                    </span>
                  </span>
                </Radio>
              </Radio.Group>
            </Form.Item>
          </div>
        }

        {/* Fuera de la rejilla de etiquetas: es una decisión, no un dato más */}
        <div className={`ota-upload__latest ${values.setLatest ? 'ota-upload__latest--on' : ''}`}>
          <Form.Item name="setLatest" valuePropName="checked" noStyle>
            <Switch />
          </Form.Item>

          <div
            className="ota-upload__latest-text"
            onClick={() => !ui.uploading && form.setFieldValue('setLatest', !values.setLatest)}
          >
            <span className="ota-upload__latest-title">
              <CheckCircleFilled className={values.setLatest ? 'ota-upload__latest-on' : 'ota-upload__latest-off'} />
              {lang.handle('Poner en distribución')}
            </span>
            <span className="ota-upload__latest-hint">
              {lang.handle('Los dispositivos del canal se actualizarán a esta versión')}
            </span>
          </div>
        </div>

        {values.setLatest &&
          <Alert
            type="warning"
            showIcon
            className="ota-upload__warning"
            message={lang.handle('Al publicarla, los equipos del canal empezarán a descargarla')}
          />
        }

        {ui.uploading &&
          <div className="ota-upload__progress">
            <Progress percent={ui.progress} status="active" />
            <Text type="secondary">
              {ui.progress < 100
                ? lang.handle('Subiendo el paquete')
                : lang.handle('Procesando en el servidor: checksum y manifest')}
            </Text>
          </div>
        }
      </Form>
    </Modal>
  );
}

export default withLocale(OtaUploadModal);
